var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useEffect, useRef, useState } from 'react';
import { DAY_BRIEF_FAILURE_REASON, DAY_BRIEF_SEED_MESSAGE } from '../constants/dayBrief';
import { buildUserMessage } from '../helpers';
import { buildDayBriefErrorMessage, isFirstRunConversationDeleted, toDayBriefError } from '../helpers/dayBrief';
import { startFirstRunChat } from '../services/chatApi';
const noop = () => undefined;
/**
 * Runs `startDayBrief` commands from the mount handle through the widget's normal
 * new-chat path: the seed shows as the technician's message in the empty view,
 * the conversation is created, then selected so `useBlockStream` attaches. A
 * replayed conversation is not seeded locally — its history already holds the
 * server's seed.
 *
 * The empty view is written wholesale with `seedMessages(null, …)` rather than
 * cleared and appended: `removeQueries` leaves the mounted observer bound to the
 * removed query, so a clear followed by a write on the same key never renders.
 */
export function useDayBriefStart({ channel, readiness, activeConversationId, scopeGenerationRef, openWidget, startNewConversation, selectConversation, seedMessages, refetchConversations, onTermsRequired }) {
    const [pending, setPending] = useState(null);
    // The command Retry would re-run, and the scope generation it failed in. Leaving the empty view
    // bumps the generation even when `activeConversationId` stays null (New conversation from the
    // empty view), so a failure from a view the technician left is no longer retryable.
    const failedAttemptRef = useRef(null);
    const touchedEmptyViewRef = useRef(false);
    useEffect(() => channel === null || channel === void 0 ? void 0 : channel.attach(setPending), [channel]);
    // What the brief leaves in the empty view (its seed, then its error) is only for as long as the
    // technician stays there. Leaving drops it, so a later new conversation does not show an attempt
    // they walked away from and Retry stops offering it (`clearMessages` is bound to the conversation
    // being left, so nothing else clears the empty view). A result that lands after they left is
    // stale (leaving bumps the scope generation) and is ignored in `run`.
    useEffect(() => {
        if (activeConversationId === null || !touchedEmptyViewRef.current)
            return;
        touchedEmptyViewRef.current = false;
        failedAttemptRef.current = null;
        seedMessages(null, []);
    }, [activeConversationId, seedMessages]);
    // `seed` is kept on screen for a retryable failure so the retry reads as the
    // same request; without it (or when retrying cannot help) only the error shows.
    const showFailure = useCallback((command, failure, seed) => {
        touchedEmptyViewRef.current = true;
        if (failure.reason === DAY_BRIEF_FAILURE_REASON.TERMS_REQUIRED) {
            seedMessages(null, []);
            onTermsRequired();
            return;
        }
        if (failure.retryable)
            failedAttemptRef.current = { command, generation: scopeGenerationRef.current };
        const error = buildDayBriefErrorMessage(failure);
        seedMessages(null, failure.retryable && seed ? [seed, error] : [error]);
    }, [seedMessages, onTermsRequired, scopeGenerationRef]);
    const run = useCallback(({ command, resolve, reject }) => {
        failedAttemptRef.current = null;
        const generation = scopeGenerationRef.current;
        const isStale = () => scopeGenerationRef.current !== generation;
        const seed = buildUserMessage(DAY_BRIEF_SEED_MESSAGE);
        touchedEmptyViewRef.current = true;
        seedMessages(null, [seed]);
        void (() => __awaiter(this, void 0, void 0, function* () {
            try {
                const chat = yield startFirstRunChat(command);
                if (!isStale()) {
                    if (!chat.replayed)
                        seedMessages(chat.id, [seed]);
                    seedMessages(null, []);
                    selectConversation(chat.id);
                }
                // Listed even when the technician has moved on: the conversation exists either way.
                refetchConversations();
                resolve({ conversation_id: chat.id, replayed: chat.replayed });
            }
            catch (err) {
                if (isFirstRunConversationDeleted(err)) {
                    if (!isStale())
                        seedMessages(null, []);
                    resolve({ conversation_id: null, replayed: false });
                    return;
                }
                console.error('[AIChatWidget] Error starting day brief:', err);
                const failure = toDayBriefError(err);
                if (!isStale())
                    showFailure(command, failure, seed);
                reject(failure);
            }
        }))();
    }, [scopeGenerationRef, seedMessages, selectConversation, refetchConversations, showFailure]);
    useEffect(() => {
        if (!pending)
            return;
        openWidget();
        if (readiness.status === 'waiting')
            return;
        if (activeConversationId !== null) {
            startNewConversation();
            return;
        }
        setPending(null);
        if (readiness.status === 'failed') {
            showFailure(pending.command, readiness.error, null);
            pending.reject(readiness.error);
            return;
        }
        run(pending);
    }, [pending, readiness, activeConversationId, openWidget, startNewConversation, showFailure, run]);
    const retryDayBrief = useCallback(() => {
        const failed = failedAttemptRef.current;
        if (!channel || !failed || activeConversationId !== null)
            return false;
        failedAttemptRef.current = null;
        if (failed.generation !== scopeGenerationRef.current)
            return false;
        // The failure is rendered by `run`/`showFailure`; the host already heard about the first one.
        void channel.request(failed.command).catch(noop);
        return true;
    }, [channel, activeConversationId, scopeGenerationRef]);
    return { retryDayBrief };
}
